import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";
import { DocumentTable } from "../components/document-table/document-table";
import type { DocumentData, LotData } from "../components/document-table/types";
import { PdfViewer, type PdfViewerFile } from "../components/pdf-viewer";

type ZoneSummary = {
  id: number;
  name: string;
  shortName: string | null;
  type: "horizontale" | "verticale";
  interventionCount: number;
  withoutMyDocumentCount: number;
  producedDocumentCount: number;
  latestBpeDocumentCount: number;
  forecastDocumentCount: number;
};
type BuildingSummary = {
  id: number;
  name: string;
  shortName: string | null;
  zones: ZoneSummary[];
};
type FilterOption = { id: number; name: string };
type SummaryData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
  };
  buildings: BuildingSummary[];
};
type ContractIntervention = {
  id: number;
  dateIntervention: string | null;
  isMine: boolean;
  clientNames: string[];
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: SummaryData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; interventions: ContractIntervention[] };
type ScopeFilter = "" | "mine" | "accessible";

const SCOPE_OPTIONS: Array<{ value: ScopeFilter; label: string }> = [
  { value: "", label: "Toutes les interventions" },
  { value: "mine", label: "Mes interventions" },
  { value: "accessible", label: "Documents accessibles à mon contrat" },
];

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

const formatDate = (value: string) => {
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("fr-FR");
};

/** Écran 034 : avancement des études pour un contrat travaux rang 2. */
export function AvancementEtudesContrat() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null,
  );
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [summaryRefresh, setSummaryRefresh] = useState(0);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [pdfFile, setPdfFile] = useState<PdfViewerFile | null>(null);
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const lots = parseIds(lotQuery);
  const performances = parseIds(performanceQuery);
  const recentOnly = searchParams.get("recentOnly") === "true";
  const scopeParam = searchParams.get("scope");
  const scope: ScopeFilter =
    scopeParam === "mine" || scopeParam === "accessible" ? scopeParam : "";

  useEffect(() => {
    void summaryRefresh;
    if (isNil(yardId)) return;
    let cancelled = false;
    setState({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/contract", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedBuildingId(
          (current) => current ?? data.data.buildings[0]?.id ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery, summaryRefresh]);

  useEffect(() => {
    void detailRefresh;
    if (isNil(yardId) || isNil(selectedZoneId)) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/contract/zones/{zoneId}", {
        params: {
          path: { yardId, zoneId: String(selectedZoneId) },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            ...(scope ? { scope } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [
    yardId,
    selectedZoneId,
    lotQuery,
    performanceQuery,
    recentOnly,
    scope,
    detailRefresh,
  ]);

  const updateFilter = (key: "lot" | "performance", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setRecentOnly = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set("recentOnly", "true");
    else next.delete("recentOnly");
    setSearchParams(next, { replace: true });
  };
  const setScope = (value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value === "mine" || value === "accessible") next.set("scope", value);
    else next.delete("scope");
    setSearchParams(next, { replace: true });
  };

  const viewPdf = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (isNotNil(data)) setPdfFile(data.data);
    },
    [yardId],
  );
  const downloadNative = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (isNotNil(data))
        window.open(data.data.url, "_blank", "noopener,noreferrer");
    },
    [yardId],
  );
  const markBpe = useCallback(
    async (versionId: number, documentId: number) => {
      if (isNil(yardId)) return;
      const confirmed = await confirmDialog({
        title: "Marquer BPE",
        message: "Marquer cette version comme BPE ?",
        confirmLabel: "Marquer BPE",
      });
      if (!confirmed) return;
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/bpe",
        {
          params: {
            path: {
              yardId,
              documentId: String(documentId),
              versionId: String(versionId),
            },
          },
        },
      );
      if (isNotNil(error))
        toaster.error({
          title: "Version non marquée BPE",
          description: error.error,
        });
      else {
        toaster.success({ title: "Modifications enregistrées" });
        setSummaryRefresh((value) => value + 1);
        setDetailRefresh((value) => value + 1);
      }
    },
    [yardId],
  );

  if (state.status === "loading")
    return <PageMessage text="Chargement de l'avancement des études…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger l'avancement des études."
        error
      />
    );
  if (state.data.buildings.length === 0)
    return (
      <PageMessage text="Aucun bâtiment n'est configuré pour ce chantier." />
    );

  const selectedBuilding =
    state.data.buildings.find((item) => item.id === selectedBuildingId) ??
    state.data.buildings[0];
  const selectedZone =
    selectedBuilding?.zones.find((item) => item.id === selectedZoneId) ?? null;

  return (
    <div className="flex h-full flex-col gap-5 overflow-hidden p-4 lg:p-6">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Avancement des études
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez vos interventions et les documents accessibles à votre
          contrat, par bâtiment et par zone.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={lots.map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={performances.map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <div className="w-72">
          <Select
            label="Interventions affichées"
            options={SCOPE_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            value={[scope]}
            onValueChange={({ value }) => setScope(value[0])}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) => setRecentOnly(event.target.checked)}
          />
          Indices récents uniquement
        </label>
      </section>
      <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto lg:grid-cols-[240px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
        <nav aria-label="Bâtiments" className="space-y-1 lg:overflow-y-auto">
          {state.data.buildings.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setSelectedBuildingId(item.id);
                setSelectedZoneId(null);
              }}
              className={cn(
                "w-full rounded-lg px-3 py-2 text-left text-sm",
                item.id === selectedBuilding?.id
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100",
              )}
            >
              {item.shortName ?? item.name}
            </button>
          ))}
        </nav>
        <main className="min-w-0 space-y-3 lg:overflow-y-auto">
          <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
            {selectedBuilding?.name}
          </h3>
          {selectedBuilding?.zones.length === 0 && (
            <p className="text-sm text-zinc-500">
              Aucune zone dans ce bâtiment.
            </p>
          )}
          {selectedBuilding?.zones.map((zoneItem) => (
            <ZoneCard
              key={zoneItem.id}
              zone={zoneItem}
              open={zoneItem.id === selectedZoneId}
              onToggle={() =>
                setSelectedZoneId(
                  zoneItem.id === selectedZoneId ? null : zoneItem.id,
                )
              }
            />
          ))}
          {isNotNil(selectedZone) && (
            <ZoneDetail
              state={detail}
              onViewPdf={viewPdf}
              onDownloadNative={downloadNative}
              onMarkBpe={markBpe}
            />
          )}
        </main>
      </div>
      {isNotNil(pdfFile) && (
        <PdfViewer file={pdfFile} onClose={() => setPdfFile(null)} />
      )}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}

function ZoneCard({
  zone,
  open,
  onToggle,
}: {
  zone: ZoneSummary;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="w-full rounded-lg border border-zinc-200 bg-white p-4 text-left dark:border-zinc-700 dark:bg-zinc-900"
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-zinc-900 dark:text-zinc-100">
          {zone.name}
        </span>
        {open ? <CaretDownIcon /> : <CaretRightIcon />}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-zinc-600 md:grid-cols-4 dark:text-zinc-400">
        <span>{zone.interventionCount} intervention(s)</span>
        <span>{zone.withoutMyDocumentCount} sans document de mon contrat</span>
        <span>{zone.producedDocumentCount} document(s) produit(s)</span>
        <span>{zone.latestBpeDocumentCount} document(s) BPE</span>
        <span>{zone.forecastDocumentCount} document(s) prévisionnel(s)</span>
      </div>
    </button>
  );
}

type ZoneDetailProps = {
  state: DetailState;
  onViewPdf: (id: number) => void;
  onDownloadNative: (id: number) => void;
  onMarkBpe: (versionId: number, documentId: number) => void;
};
function ZoneDetail({
  state,
  onViewPdf,
  onDownloadNative,
  onMarkBpe,
}: ZoneDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette zone."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => {
        const lots: LotData[] = [
          {
            id: item.lot.id,
            name: item.lot.name,
            nLot: item.lot.nLot,
            prestas: [
              {
                id: item.performance.id,
                name: item.performance.name,
                documents: item.documents,
              },
            ],
          },
        ];
        return (
          <article
            key={item.id}
            className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="font-semibold text-zinc-900 dark:text-zinc-100">
                  {item.lot.nLot ? `Lot ${item.lot.nLot} — ` : ""}
                  {item.performance.name}
                </h4>
                {item.isMine ? (
                  <p className="mt-1 text-sm text-zinc-500">
                    {item.clientNames.length === 0
                      ? "Client non renseigné"
                      : `Client : ${item.clientNames.join(" · ")}`}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-zinc-500">
                    Documents accessibles à mon contrat
                  </p>
                )}
              </div>
              <p className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
                Date de démarrage presta
                <span className="mt-1 block text-sm font-normal">
                  {isNotNil(item.dateIntervention)
                    ? formatDate(item.dateIntervention)
                    : "—"}
                </span>
              </p>
            </div>
            <div className="mt-4">
              {item.documents.length === 0 ? (
                <p className="rounded-lg bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
                  Aucun document accessible pour cette intervention.
                </p>
              ) : (
                <DocumentTable
                  lots={lots}
                  canMarkBpe
                  onViewPdf={onViewPdf}
                  onDownloadNative={onDownloadNative}
                  onMarkBpe={onMarkBpe}
                  onOpenSynthese={() => undefined}
                  onOpenDestinataires={() => undefined}
                  onOpenDiffusion={() => undefined}
                  onOpenInterne={() => undefined}
                  showDiffusionActions={false}
                />
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
